import { Text, View, ScrollView, Pressable, TouchableOpacity, RefreshControl, useWindowDimensions } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Toast from 'react-native-toast-message';
import usePlatform from '@/utils/hooks/usePlatform';
import { DateTime } from 'luxon';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { showAlert } from '@/utils/showAlert';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';
import { deleteEvent } from '@/data/events';
import { TabView } from 'react-native-tab-view';
import { StyleSheet } from 'react-native-unistyles';
import Callout from '@/components/General/Callout';
import Chart from '@/components/Charts/Chart';
import KPICard from '@/components/Charts/KPICard';
import SelectInput from '@/components/General/inputs/SelectInput';
import Unicon from '@/components/General/Unicon';
import PageTitle from '@/components/General/PageTitle';
import ReachTabBar from '@/components/General/ReachTabBar';
import { isServiceFlowV2Available } from '@/config/serviceFlows';


const UniqueAttendeesCard = ({ eventID, numberOfDays, count }) => {
    const [enabled, setEnabled] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const { data, isLoading } = Query.events.uniqueAttendeesDetail({
        eventID,
        numberOfDays,
        options: { enabled },
    });

    const details = useMemo(() => ({
        columns: [
            { key: 'name', label: 'Name', width: 150, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
            { key: 'last_attended', label: 'Last Attended', width: 130 },
            { key: 'times_attended', label: 'Times', width: 60 },
        ],
        data: data?.map(attendee => ({
            person_id: attendee.person_id,
            name: `${attendee.first_name} ${attendee.last_name}`,
            first_name: attendee.first_name,
            last_name: attendee.last_name,
            email: attendee.email,
            phones: attendee.phones,
            last_attended: attendee.last_attended ? DateTime.fromISO(attendee.last_attended).toLocaleString(DateTime.DATE_MED) : '—',
            times_attended: attendee.times_attended,
        })) || [],
    }), [data]);

    useEffect(() => {
        if (enabled && !isLoading && data) {
            setModalOpen(true);
        }
    }, [enabled, isLoading, data]);

    const handleDetailsOpen = () => {
        if (data) {
            setModalOpen(true);
        } else {
            setEnabled(true);
        }
    };

    useEffect(() => {
        setEnabled(false);
        setModalOpen(false);
    }, [numberOfDays]);

    return (
        <KPICard
            title="Unique Attendees"
            content={count}
            detailsTitle="Breakdown"
            details={details}
            onDetailsOpen={handleDetailsOpen}
            detailsLoading={isLoading}
            modalOpen={modalOpen}
            onModalClose={() => setModalOpen(false)}
        />
    );
};


const EventSeriesScreen = (props) => {
    const params = useLocalSearchParams();
    const eventID = props.eventID || params.eventID;

    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { width } = useWindowDimensions();

    const [notFound, setNotFound] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [numberOfDays, setNumberOfDays] = useState(30);

    const { isNative, isWeb } = usePlatform();
    const isWideScreen = width >= 900;

    // ============ QUERIES ============
    const { data: event, isFetching: fetchingEvent } = Query.events.byId({ id: eventID });
    const { data: analytics, isLoading: fetchingAnalytics } = Query.events.analytics({ eventID: eventID, numberOfDays: numberOfDays, options: { enabled: tabIndex === 0 || (isWideScreen && tabIndex === 0) } });
    const analyticsTabActive = tabIndex === 0 || isWideScreen;
    const { data: uniqueAttendees } = Query.events.uniqueAttendees({ eventID: eventID, numberOfDays: numberOfDays, options: { enabled: analyticsTabActive } });
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID });
    const serviceFlowV2 = isServiceFlowV2Available(organization);
    const { data: serviceFlowAttachment, isError: attachmentError } = Query.serviceFlows.eventAttachment({
        organizationID: loggedInUser?.organizationID,
        accountID: loggedInUser?.accountID,
        eventID,
        options: { enabled: serviceFlowV2 && !!eventID },
    });
    const saveShortcut = useSaveShortcut(loggedInUser?.id);
    const pastInstancesQuery = Query.eventInstances.byEventId({ eventID: eventID, options: { enabled: tabIndex === 1 || (isWideScreen && tabIndex === 0) } });
    const pastInstances = pastInstancesQuery.data?.pages?.flatMap(p => p.data) || [];

    // ============ MUTATIONS ============
    const deleteEventMutation = useMutation({
        mutationFn: deleteEvent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
            queryClient.setQueryData(QueryKeys.events.all(), (oldData) => {
                return oldData?.filter(e => e.id !== eventID);
            });
            Toast.show({ type: 'success', text2: `The event series was deleted successfully` });
            router.back();
        }
    });

    // ============ DERIVED DATA ============
    const serviceFlowTasks = useMemo(() => event?.Tasks?.filter(t => t?.dueType === 'EVENT_SERVICE_FLOW') || [], [event?.Tasks]);
    const volunteerTasks = useMemo(() => event?.Tasks?.filter(t => t?.dueType === 'EVENT_VOLUNTEER') || [], [event?.Tasks]);

    const formattedDefaultTime = useMemo(() => {
        if (!event?.defaultStartTime) return '';
        return DateTime.fromISO(event.defaultStartTime).toLocaleString(DateTime.TIME_SIMPLE);
    }, [event?.defaultStartTime]);

    const formattedDefaultDate = useMemo(() => {
        if (!event?.defaultStartTime) return '';
        return DateTime.fromISO(event.defaultStartTime).toLocaleString(DateTime.DATE_MED);
    }, [event?.defaultStartTime]);

    // ============ EFFECTS ============
    useEffect(() => {
        if (loggedInUser) {
            if (!eventID || (!event?.id && !fetchingEvent)) {
                setNotFound("We couldn't find that event series");
            } else if (!loggedInUser?.can?.("View Events") && loggedInUser?.id !== eventID) {
                setNotFound("You don't have permission to view this event");
            } else {
                setNotFound(null);
            }
        }
    }, [fetchingEvent, loggedInUser, eventID, event?.id]);

    useEffect(() => {
        const options = [];

        if (event?.id) {
            options.push({
                name: "Save to Shortcuts",
                icon: dropdownIcons.favorite,
                onSelect: () => saveShortcut({ screen: `/events/${event.id}`, label: event?.name || 'Event Series', icon: 'calendar-outline' })
            });
        }

        if (loggedInUser?.can?.("Edit Event Series") || (loggedInUser?.id === event?.id && loggedInUser?.can?.("Edit Events"))) {
            options.push({
                name: "Edit Event Series",
                icon: dropdownIcons.edit,
                onSelect: () => router.push({ pathname: "/events/create", params: { id: event?.id } })
            });
        }

        if (loggedInUser?.can?.("Delete Event Series")) {
            options.push({
                name: "Delete Event Series",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => {
                    showAlert({
                        title: `Delete ${event?.name}?`,
                        subtitle: "This will delete all occurrences and cannot be undone.",
                        onConfirm: () => deleteEventMutation.mutate(event?.id),
                        isDestructive: true,
                        confirmText: "Delete"
                    });
                }
            });
        }

        navigation.setOptions({
            headerTitle: () => <PageTitle title={event?.name} subtitle="Event Series" />,
            headerRight: options.length === 0 || notFound ? null : () => <DropdownMenu testID="HeaderMenu" options={options} />,
        });
    }, [navigation, event, notFound, loggedInUser, saveShortcut]);

    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.events.byId(eventID) });

    // ============ TAB ROUTES ============
    const tabRoutes = useMemo(() => {
        const routes = [{ key: 'info', title: 'Info' }];
        routes.push({ key: 'analytics', title: 'Analytics' });
        routes.push({ key: 'past', title: 'Past' });
        if (serviceFlowTasks.length > 0 || volunteerTasks.length > 0) routes.push({ key: 'tasks', title: 'Tasks' });
        return routes;
    }, [serviceFlowTasks.length, volunteerTasks.length]);

    // ============ OCCURRENCE ITEM COMPONENT ============
    const OccurrenceItem = useCallback(({ instance, previousDate }) => {
        const instanceDate = DateTime.fromISO(instance.startTime);
        const previousInstanceDate = previousDate ? DateTime.fromISO(previousDate) : null;
        const showYearSeparator = !previousInstanceDate || !previousInstanceDate.hasSame(instanceDate, 'year');
        return (<>
            {showYearSeparator && (
                <View style={styles.yearSeparator}>
                    <Text style={styles.yearSeparatorText}>{instanceDate.toFormat('yyyy')}</Text>
                </View>
            )}
            <Pressable
                onPress={() => router.push(`/events/instances/${instance.id}`)}
                style={styles.occurrenceItem}
            >
                <View style={styles.occurrenceDateBadge}>
                    <Text style={styles.occurrenceDateMonth}>{instanceDate.toFormat('MMM').toUpperCase()}</Text>
                    <Text style={styles.occurrenceDateDay}>{instanceDate.day}</Text>
                </View>
                <View style={styles.occurrenceInfo}>
                    <Text style={[styles.occurrenceTitle, instance.canceled && styles.strikethrough]}>
                        {instance.nameOverride || instance.Event?.name}
                    </Text>
                    <Text style={styles.occurrenceSubtitle}>
                        {instanceDate.toFormat('cccc')} • {instanceDate.toLocaleString(DateTime.TIME_SIMPLE)}
                    </Text>
                </View>
                <View style={styles.occurrenceRight}>
                    {instance.canceled && (
                        <View style={styles.canceledBadge}>
                            <Text style={styles.canceledBadgeText}>Canceled</Text>
                        </View>
                    )}
                    <View style={styles.attendanceBadge}>
                        <Unicon name="people" size={12} colorVariant="textSecondary" />
                        <Text style={styles.attendanceBadgeText}>{instance.attendanceCount}</Text>
                    </View>
                    <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                </View>
            </Pressable>
        </>);
    }, []);

    // ============ INFO ROW COMPONENT ============
    const InfoRow = useCallback(({ icon, label, value, onPress, children, isEmpty }) => {
        if (!value && !children && !isEmpty) return null;
        const content = (
            <View style={styles.infoRow}>
                <View style={styles.infoRowLeft}>
                    <Unicon name={icon} size={18} colorVariant="textSecondary" />
                    <Text style={styles.infoRowLabel}>{label}</Text>
                </View>
                <View style={styles.infoRowRight}>
                    {children || <Text style={[styles.infoRowValue, onPress && styles.linkText, isEmpty && styles.emptyText]}>{value}</Text>}
                </View>
            </View>
        );
        return onPress ? <TouchableOpacity onPress={onPress}>{content}</TouchableOpacity> : content;
    }, []);

    // ============ TASK CARD COMPONENT ============
    const TaskItem = useCallback(({ task }) => (
        <Pressable onPress={() => router.push({ pathname: `/tasks/${task?.id}` })} style={styles.taskItem}>
            <View style={styles.taskItemLeft}>
                <View style={styles.taskIconContainer}>
                    <Unicon name={task?.dueType === 'EVENT_SERVICE_FLOW' ? 'list' : 'people'} size={18} colorVariant="primary" />
                </View>
                <View style={styles.taskItemInfo}>
                    <Text style={styles.taskItemTitle}>{task?.title}</Text>
                    {task?.description && <Text style={styles.taskItemDesc} numberOfLines={1}>{task?.description}</Text>}
                    {task?.Location?.id && (
                        <TouchableOpacity
                            style={styles.locationBadge}
                            onPress={(e) => { e.stopPropagation?.(); router.push('/locations/' + task.Location.id); }}
                        >
                            <Unicon name="location" size={10} colorVariant="secondary" />
                            <Text style={styles.locationBadgeText}>{task.Location.name}</Text>
                        </TouchableOpacity>
                    )}
                </View>
            </View>
            <View style={styles.taskItemRight}>
                {task?.default && (
                    <View style={styles.defaultBadge}>
                        <Unicon name="checkmark" size={12} colorVariant="textSecondary" />
                        <Text style={styles.defaultBadgeText}>Default</Text>
                    </View>
                )}
                {(task?.printLabelID || task?.printFamilyLabelID) && <Unicon name="print-outline" size={16} colorVariant="textSecondary" />}
                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
            </View>
        </Pressable>
    ), []);

    // ============ SERIES INFO PANEL ============
    const SeriesInfoPanel = useCallback(() => (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                refreshControl={<RefreshControl refreshing={fetchingEvent} onRefresh={refresh} />}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
            >
                {/* Header Card */}
                <View style={styles.headerCard}>
                    {/* Series Badge */}
                    <View style={styles.seriesBadge}>
                        <Unicon name="repeat" size={14} colorVariant="events" />
                        <Text style={styles.seriesBadgeText}>Recurring Event Series</Text>
                    </View>

                    <View style={styles.eventTitleRow}>
                        <View style={styles.seriesIconBadge}>
                            <Unicon name="layers" size={24} colorVariant="events" />
                        </View>
                        <View style={styles.eventTitleInfo}>
                            <Text style={styles.eventTitle}>{event?.name}</Text>
                            <Text style={styles.eventSubtitle}>{event?.Calendar?.name}</Text>
                        </View>
                    </View>

                    {/* Quick Stats */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Avg Attendance</Text>
                            <Text style={styles.statValue}>{analytics?.avg || '-'}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Occurrences</Text>
                            <Text style={styles.statValue}>{event?.pastInstanceCount || 0}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Tasks</Text>
                            <Text style={styles.statValue}>{serviceFlowTasks.length + volunteerTasks.length}</Text>
                        </View>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        {loggedInUser?.can?.("Edit Event Series") && (
                            <TouchableOpacity
                                style={styles.actionBtn}
                                onPress={() => router.push({ pathname: "/events/create", params: { id: event?.id } })}
                            >
                                <Unicon name="create" size={16} colorVariant="text" />
                                <Text style={styles.actionBtnText}>Edit Series</Text>
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity
                            style={styles.actionBtn}
                            onPress={() => router.push('/events')}
                        >
                            <Unicon name="calendar" size={16} colorVariant="text" />
                            <Text style={styles.actionBtnText}>View Occurrences</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Default Settings Section */}
                <View style={styles.infoSection}>
                    <Text style={styles.sectionLabel}>Default Settings</Text>

                    <InfoRow icon="time" label="Default Time" value={formattedDefaultTime || 'Not set'} isEmpty={!formattedDefaultTime} />

                    <InfoRow icon="calendar" label="Default Date" value={formattedDefaultDate || 'Not set'} isEmpty={!formattedDefaultDate} />

                    <InfoRow icon="albums" label="Calendar" value={event?.Calendar?.name || 'None'} isEmpty={!event?.Calendar?.name}>
                        {event?.Calendar && (
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <View style={[styles.calendarDot(event.Calendar.color), { marginTop: 0 }]} />
                                {loggedInUser?.can?.("Edit Calendars") ? (
                                    <TouchableOpacity onPress={() => router.push({ pathname: "/events/calendars/create", params: { id: event.Calendar.id } })}>
                                        <Text style={[styles.infoRowValue, styles.linkText, styles.underlineText]}>{event.Calendar.name}</Text>
                                        {event.Calendar.description && <Text style={styles.infoRowValueSub}>{event.Calendar.description}</Text>}
                                    </TouchableOpacity>
                                ) : (
                                    <View>
                                        <Text style={styles.infoRowValue}>{event.Calendar.name}</Text>
                                        {event.Calendar.description && <Text style={styles.infoRowValueSub}>{event.Calendar.description}</Text>}
                                    </View>
                                )}
                            </View>
                        )}
                    </InfoRow>

                    <InfoRow icon="mic" label="Default Speaker" value={event?.defaultSpeaker ? `${event.defaultSpeaker.firstName} ${event.defaultSpeaker.lastName}` : 'None'} isEmpty={!event?.defaultSpeaker} />

                    {serviceFlowV2 && (
                        <InfoRow
                            icon="list"
                            label="Service Flow"
                            value={attachmentError ? 'Could not load templates' : !serviceFlowAttachment ? 'Loading…' : serviceFlowAttachment?.serviceFlowTemplateID ? (serviceFlowAttachment?.ServiceFlowTemplate?.name || 'Template') : 'None'}
                            isEmpty={!serviceFlowAttachment?.serviceFlowTemplateID}
                            onPress={serviceFlowAttachment?.serviceFlowTemplateID
                                ? () => router.push(`/events/service-flow/${serviceFlowAttachment.serviceFlowTemplateID}`)
                                : () => router.push('/events/service-flow')}
                        />
                    )}
                    {serviceFlowV2 && serviceFlowAttachment?.locations?.map(binding => {
                        // bindingVersion 0 means no row exists — implicit
                        // bindings display as 'None' to match the edit form's
                        // explicit-None default.
                        const implicit = !binding.isOverride && (binding.bindingVersion ?? 0) === 0;
                        return <InfoRow key={binding.locationID} icon="list"
                            label={`Service Flow — ${binding.locationName || 'Location'}`}
                            value={implicit ? 'None' : binding.isOverride ? (binding.templateID ? (binding.ServiceFlowTemplate?.name || 'Template unavailable') : 'None') : `Inherit default: ${binding.ServiceFlowTemplate?.name || 'None'}`}
                            onPress={!implicit && binding.effectiveTemplateID ? () => router.push(`/events/service-flow/${binding.effectiveTemplateID}`) : undefined} />;
                    })}

                    <InfoRow icon="location" label="Locations & Check-In Filters" isEmpty={!event?.Locations?.length}>
                        {event?.Locations?.length > 0 ? (
                            <View>
                                {event.Locations.map((loc, idx) => (
                                    <TouchableOpacity
                                        key={idx}
                                        style={{ marginBottom: idx < event.Locations.length - 1 ? 8 : 0 }}
                                        onPress={() => loc?.Location?.id && router.push('/locations/' + loc.Location.id)}
                                        disabled={!loc?.Location?.id}
                                    >
                                        <Text style={[styles.infoRowValue, loc?.Location?.id && styles.linkText]}>{loc?.Location?.name}</Text>
                                        {loc?.CheckInFilter ? (
                                            <Text style={styles.infoRowValueSub}>Filter: {loc.CheckInFilter.name}</Text>
                                        ) : (
                                            <Text style={[styles.infoRowValueSub]}>No filter</Text>
                                        )}
                                    </TouchableOpacity>
                                ))}
                            </View>
                        ) : (
                            <Text style={styles.emptyText}>None</Text>
                        )}
                    </InfoRow>
                </View>

                {/* Notes Section */}
                {event?.defaultNotes && (
                    <View style={styles.notesSection}>
                        <Text style={styles.sectionLabel}>Default Notes</Text>
                        <View style={styles.notesCard}>
                            <Text style={styles.notesText}>{event.defaultNotes}</Text>
                        </View>
                    </View>
                )}
            </ScrollView>
        </View>
    ), [event, fetchingEvent, loggedInUser, serviceFlowTasks.length, volunteerTasks.length, formattedDefaultTime, formattedDefaultDate, InfoRow, analytics, serviceFlowV2, serviceFlowAttachment, attachmentError]);

    // ============ TAB SCENES ============
    const renderTabBar = useCallback((props) => <ReachTabBar {...props} scrollEnabled={tabRoutes.length > 4} />, [tabRoutes.length]);

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'info':
                return <SeriesInfoPanel />;

            case 'analytics':
                return fetchingAnalytics ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator size="large" />
                    </View>
                ) : (
                    <ScrollView style={styles.tabContent} contentContainerStyle={styles.listPadding} keyboardShouldPersistTaps="handled"
>
                        <View style={styles.filterContainer}>
                            <SelectInput
                                title="Time Range"
                                options={[
                                    { label: "Last 7 Days", value: 7 },
                                    { label: "Last 30 Days", value: 30 },
                                    { label: "Last 90 Days", value: 90 },
                                    { label: "Last 6 Months", value: 180 },
                                    { label: "Last Year", value: 365 },
                                    { label: "All Time", value: 10000 },
                                ]}
                                value={numberOfDays}
                                hideClearButton
                                onUpdate={setNumberOfDays}
                            />
                        </View>

                        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.kpiScroll} keyboardShouldPersistTaps="handled">
                            <View style={styles.kpiRow}>
                                <UniqueAttendeesCard eventID={event?.id} numberOfDays={numberOfDays} count={uniqueAttendees || 0} />
                                <KPICard title="Average Attendance" content={analytics?.avg || 0} />
                                <KPICard title="Highest Attendance" content={analytics?.max || 0} />
                                <KPICard title="Lowest Attendance" content={analytics?.min || 0} />
                            </View>
                        </ScrollView>

                        <View style={styles.chartContainer}>
                            <Chart
                                height={isWeb ? 400 : 300}
                                chartName="EventAttendanceChart"
                                parameters={{
                                    eventID: event?.id,
                                    startDate: (organization?.timezone ? DateTime.now().setZone(organization.timezone) : DateTime.now()).minus({ days: numberOfDays }).toFormat("yyyy-MM-dd"),
                                    endDate: (organization?.timezone ? DateTime.now().setZone(organization.timezone) : DateTime.now()).toFormat("yyyy-MM-dd")
                                }}
                            />
                        </View>
                    </ScrollView>
                );

            case 'past':
                return (
                    <View style={styles.tabContent}>
                        {pastInstancesQuery.isLoading ? (
                            <View style={styles.loadingContainer}>
                                <ActivityIndicator size="large" />
                            </View>
                        ) : pastInstances.length === 0 ? (
                            <EmptyListPlaceholder message="No past occurrences yet" />
                        ) : (
                            <ScrollView
                                style={styles.tabContent}
                                contentContainerStyle={styles.listPadding}
                                keyboardShouldPersistTaps="handled"
                                onScroll={({ nativeEvent }) => {
                                    const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
                                    const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 100;
                                    if (isCloseToBottom && pastInstancesQuery.hasNextPage && !pastInstancesQuery.isFetchingNextPage) {
                                        pastInstancesQuery.fetchNextPage();
                                    }
                                }}
                                scrollEventThrottle={400}
                            >
                                {pastInstances.map((instance) => {
                                    const previousDate = pastInstances[pastInstances.indexOf(instance) - 1]?.startTime;
                                    return <OccurrenceItem key={instance.id} instance={instance} previousDate={previousDate} />;
                                })}
                                {pastInstancesQuery.isFetchingNextPage && (
                                    <View style={{ padding: 20, alignItems: 'center' }}>
                                        <ActivityIndicator size="small" />
                                    </View>
                                )}
                            </ScrollView>
                        )}
                    </View>
                );

            case 'tasks':
                return (
                    <ScrollView style={styles.tabContent} contentContainerStyle={styles.taskListPadding} keyboardShouldPersistTaps="handled"
>
                        <Callout
                            type="info"
                            title="Series Tasks"
                            message="These tasks are attached to the event series. A task instance is created for each new occurrence."
                        />

                        {serviceFlowTasks.length > 0 && (
                            <View style={styles.taskSection}>
                                <Text style={styles.taskSectionTitle}>Service Flow</Text>
                                {serviceFlowTasks.map((task) => <TaskItem key={task.id} task={task} />)}
                            </View>
                        )}

                        {volunteerTasks.length > 0 && (
                            <View style={styles.taskSection}>
                                <Text style={styles.taskSectionTitle}>Volunteer Tasks</Text>
                                {Object.entries(
                                    volunteerTasks.reduce((acc, task) => {
                                        const teamID = task.volunteerTeamID || 'ungrouped';
                                        const teamName = task.VolunteerTeam?.name || 'Ungrouped';
                                        if (!acc[teamID]) acc[teamID] = { name: teamName, tasks: [] };
                                        acc[teamID].tasks.push(task);
                                        return acc;
                                    }, {})
                                ).map(([teamID, { name, tasks }]) => (
                                    <View key={teamID} style={styles.volunteerTeamGroup}>
                                        <Text style={styles.volunteerTeamName}>{name}</Text>
                                        {tasks.map((task) => (
                                            <TaskItem key={task.id} task={task} />
                                        ))}
                                    </View>
                                ))}
                            </View>
                        )}

                        {serviceFlowTasks.length === 0 && volunteerTasks.length === 0 && (
                            <EmptyListPlaceholder message="No tasks attached to this series" />
                        )}
                    </ScrollView>
                );

            default:
                return null;
        }
    }, [event, fetchingAnalytics, analytics, uniqueAttendees, numberOfDays, serviceFlowTasks, volunteerTasks, isWeb, SeriesInfoPanel, TaskItem, pastInstances, pastInstancesQuery, OccurrenceItem]);

    // ============ WIDE SCREEN LAYOUT ============
    const WideScreenLayout = useCallback(() => {
        const activityRoutes = tabRoutes.filter(r => r.key !== 'info');

        return (
            <View style={styles.twoColumn}>
                <SeriesInfoPanel />
                {activityRoutes.length > 0 && (
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: tabIndex, routes: activityRoutes }}
                            renderScene={renderScene}
                            onIndexChange={setTabIndex}
                            renderTabBar={renderTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                )}
            </View>
        );
    }, [tabRoutes, tabIndex, SeriesInfoPanel, renderScene, renderTabBar, isNative]);

    // ============ MAIN RENDER ============
    if (notFound) return <EmptyListPlaceholder type="404" message={notFound} />;
    if (fetchingEvent && !event) return <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View>;

    if (isWideScreen) return <View style={styles.container}><WideScreenLayout /></View>;

    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: tabIndex, routes: tabRoutes }}
                renderScene={renderScene}
                onIndexChange={setTabIndex}
                renderTabBar={renderTabBar}
                swipeEnabled
                lazy
            />
        </View>
    );
};


const styles = StyleSheet.create((theme) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2 },
    tabContent: { flex: 1 },
    listPadding: { padding: 16, paddingBottom: 100 },
    taskListPadding: { padding: 16, paddingBottom: 100, maxWidth: 800, width: '100%', alignSelf: 'center' },

    // Header Card
    headerCard: { backgroundColor: theme.colors.surface, borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: theme.shadows.card },

    // Series Badge - Distinctive indicator that this is a series
    seriesBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.eventsContainer, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, marginBottom: 16, alignSelf: 'flex-start', gap: 6 },
    seriesBadgeText: { color: theme.colors.events, fontSize: 12, fontFamily: theme.fonts.bold },

    eventTitleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
    seriesIconBadge: { width: 52, height: 52, backgroundColor: theme.colors.eventsContainer, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
    seriesIconColor: { color: theme.colors.events },
    eventTitleInfo: { flex: 1, marginLeft: 14 },
    eventTitle: { fontSize: 18, fontFamily: theme.fonts.heading, color: theme.colors.text },
    eventSubtitle: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 },

    // Stats Row
    statsRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderTopWidth: 1, borderBottomWidth: 1, borderColor: theme.colors.border, marginBottom: 16 },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    actionBtnPrimary: { flex: 1, minWidth: 100, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, backgroundColor: theme.colors.primary },
    actionBtnPrimaryText: { color: theme.colors.surface, fontSize: 13, fontFamily: theme.fonts.bold },
    actionBtn: { flex: 1, minWidth: 100, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
    actionBtnText: { color: theme.colors.text, fontSize: 13, fontFamily: theme.fonts.bold },

    // Info Section
    infoSection: { backgroundColor: theme.colors.surface, borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: theme.shadows.card },
    sectionLabel: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 12 },
    infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 140 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, alignItems: 'flex-end' },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    infoRowValueSub: { fontSize: 12, color: theme.colors.textSecondary, textAlign: 'right', marginTop: 2, fontStyle: 'italic' },
    linkText: { color: theme.colors.events },
    underlineText: { textDecorationLine: 'underline' },
    calendarDot: (color) => ({ marginTop: 2, width: 8, height: 8, borderRadius: 6, backgroundColor: color }),
    emptyText: { fontSize: 14, color: theme.colors.textSecondary, fontStyle: 'italic', textAlign: 'right' },

    // Notes Section
    notesSection: { marginBottom: 16 },
    notesCard: { backgroundColor: theme.colors.surface, borderRadius: 12, padding: 16, boxShadow: theme.shadows.card },
    notesText: { fontSize: 14, color: theme.colors.text, lineHeight: 22 },

    // Tasks Section
    taskSection: { marginVertical: 12 },
    taskSectionTitle: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 12 },
    volunteerTeamGroup: { marginBottom: 16 },
    volunteerTeamName: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text, marginBottom: 8 },
    taskItem: { backgroundColor: theme.colors.surface, borderRadius: 10, padding: 14, marginBottom: 8, boxShadow: theme.shadows.card, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    taskItemLeft: { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 12 },
    taskIconContainer: { width: 36, height: 36, borderRadius: 8, backgroundColor: theme.colors.primaryContainer, alignItems: 'center', justifyContent: 'center' },
    taskItemInfo: { flex: 1 },
    taskItemTitle: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text },
    taskItemDesc: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },
    taskItemRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    defaultBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.successContainer, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, gap: 4 },
    defaultBadgeText: { fontSize: 10, fontFamily: theme.fonts.bold, color: theme.colors.success },
    locationBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.secondaryContainer, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, gap: 3, marginTop: 4, alignSelf: 'flex-start' },
    locationBadgeText: { fontSize: 10, fontFamily: theme.fonts.bold, color: theme.colors.secondary },
    iconMuted: { color: theme.colors.textSecondary },

    // Analytics
    filterContainer: { marginBottom: 16, maxWidth: 300 },
    kpiScroll: { marginBottom: 16 },
    kpiRow: { flexDirection: 'row', gap: 12 },
    chartContainer: { boxShadow: theme.shadows.card },

    // Past Occurrences
    occurrenceItem: { backgroundColor: theme.colors.surface, borderRadius: 10, padding: 14, marginBottom: 8, boxShadow: theme.shadows.card, flexDirection: 'row', alignItems: 'center', gap: 12 },
    occurrenceDateBadge: { width: 44, height: 44, backgroundColor: theme.colors.primaryContainer, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
    occurrenceDateMonth: { fontSize: 9, fontFamily: theme.fonts.bold, color: theme.colors.primary },
    occurrenceDateDay: { fontSize: 18, fontFamily: theme.fonts.bold, color: theme.colors.primary, marginTop: -2 },
    occurrenceInfo: { flex: 1 },
    occurrenceTitle: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text },
    occurrenceSubtitle: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },
    occurrenceRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    strikethrough: { textDecorationLine: 'line-through', textDecorationStyle: 'solid' },
    canceledBadge: { backgroundColor: theme.colors.errorContainer, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
    canceledBadgeText: { fontSize: 10, fontFamily: theme.fonts.bold, color: theme.colors.error },
    attendanceBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.surfaceVariant, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, gap: 4 },
    attendanceBadgeText: { fontSize: 11, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary },
    yearSeparator: { paddingVertical: 8 },
    yearSeparatorText: { fontSize: 16, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary }
}));


export default EventSeriesScreen;
